import React, { useState, useEffect, useRef } from 'react';
import Icon from '../shared/Icon';
import Chip from '../shared/Chip';
import { apiService } from '../../../services/api';
import './account-modals.css';

const CATEGORIES = [
  'Bug',
  'Data issue',
  'Automation failure',
  'Feature request',
  'Billing question',
  'Security or privacy concern',
  'Other',
];
const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];
const SECURITY_CATEGORY = 'Security or privacy concern';
const ATTACH_ACCEPT = 'image/*';
const APP_VERSION = process.env.REACT_APP_VERSION || '—';

/**
 * Get Help / in-app support form (PRD-648). Frontend UI per the workspace prototype:
 * category, subject, description, priority, optional attachment. Security/privacy reports
 * show a routing note.
 *
 * NOTE: submission is UI-only for now — the backend support intake (Jira issue creation)
 * is a follow-up. On submit we validate + confirm + close; nothing is sent to a server yet.
 */
function GetHelpModal({ isOpen, user, pageTitle, onClose, onShowNotification }) {
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('Medium');
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [ctxOpen, setCtxOpen] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedKey, setSubmittedKey] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setCategory(CATEGORIES[0]);
    setSubject('');
    setDescription('');
    setPriority('Medium');
    setFiles([]);
    setDragOver(false);
    setCtxOpen(true);
    setError('');
    setSubmitting(false);
    setSubmitted(false);
    setSubmittedKey('');
  }, [isOpen]);

  if (!isOpen) return null;

  // Context attached automatically with every request (like the Bridge support panel).
  const orgName = user?.org_display_name || user?.org_name || '—';
  const userEmail = user?.email || '—';
  const pathPage = (typeof window !== 'undefined' && window.location?.pathname
    ? window.location.pathname.replace(/^\/+/, '')
    : '');
  const currentPage = pageTitle || pathPage || 'home';

  const addFiles = (fileList) => {
    const picked = Array.from(fileList || []);
    if (picked.length) setFiles((prev) => [...prev, ...picked]);
  };

  const handleSubmit = async () => {
    setError('');
    if (!category) return setError('Choose a type so we can route it to the right team.');
    if (!subject.trim()) return setError('Add a subject so support can scan it quickly.');
    if (!description.trim()) return setError('Tell us what happened so we can act on it.');
    setSubmitting(true);
    try {
      const result = await apiService.createSupportTicket(
        {
          category,
          subject: subject.trim(),
          description: description.trim(),
          priority,
          current_page: currentPage,
          app_version: APP_VERSION,
        },
        files,
      );
      setSubmittedKey(result?.tracking_id || result?.key || '');
      setSubmitted(true);
      const ticket = result?.key || '';
      onShowNotification?.(ticket ? `Support request submitted (${ticket})` : 'Support request submitted');
    } catch (e) {
      setError(e?.message || 'Could not submit your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="account-modal-overlay" onMouseDown={onClose}>
      <div
        className="account-modal"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Get help"
      >
        <div className="account-modal-header">
          <div>
            <div className="account-modal-eyebrow">Support</div>
            <h2 className="account-modal-title">Get Help</h2>
            <p className="account-modal-sub">
              Tell us what is happening — we will capture the technical details for you
            </p>
          </div>
          <button type="button" className="account-modal-close" onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
        </div>
        {submitted ? (
          <>
            <div className="account-modal-body">
              <h3 className="account-success-title">Support request submitted</h3>
              <p className="account-success-sub">
                Our team has received your request and will follow up by email.
              </p>
              {submittedKey && (
                <p className="account-success-ref">
                  <strong>Tracking number:</strong> {submittedKey}
                </p>
              )}
            </div>
            <div className="account-modal-footer">
              <button type="button" className="account-btn account-btn-primary" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        ) : (
        <>
        <div className="account-modal-body">
          {error && (
            <div className="account-modal-error" role="alert">
              {error}
            </div>
          )}

          <label className="resui-field">
            <span className="resui-field-label">
              What kind of issue is this? <span className="account-req">*</span>
            </span>
            <select
              className="resui-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>
          {category === SECURITY_CATEGORY && (
            <div className="account-note account-note-security">
              <Icon name="shield" size={18} />
              <span>
                Security and privacy reports are routed on a priority path and handled separately from
                general requests.
              </span>
            </div>
          )}

          <label className="resui-field">
            <span className="resui-field-label">
              Subject <span className="account-req">*</span>
            </span>
            <input
              className="resui-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="A short summary of the problem"
            />
          </label>

          <label className="resui-field">
            <span className="resui-field-label">
              Description <span className="account-req">*</span>
            </span>
            <textarea
              className="resui-textarea"
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What happened, what you expected, and any steps to reproduce it."
            />
          </label>

          <label className="resui-field">
            <span className="resui-field-label">Priority</span>
            <select
              className="resui-select"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </label>

          <div className="resui-field">
            <span className="resui-field-label">
              Attachments <span className="account-opt">optional</span>
            </span>
            <button
              type="button"
              className={`account-drop ${dragOver ? 'is-over' : ''}`}
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                addFiles(e.dataTransfer.files);
              }}
            >
              <Icon name="upload" size={22} className="account-drop-icon" />
              <span>Drop a screenshot or click to add</span>
            </button>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept={ATTACH_ACCEPT}
              onChange={(e) => addFiles(e.target.files)}
              style={{ display: 'none' }}
            />
            {files.length > 0 && (
              <div className="account-file-list">
                {files.map((f, i) => (
                  <Chip
                    key={`${f.name}-${i}`}
                    className="account-file-chip"
                    onDismiss={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                  >
                    {f.name}
                  </Chip>
                ))}
              </div>
            )}
          </div>

          {/* Captured automatically — technical context attached with the request. */}
          <div className={`account-ctx ${ctxOpen ? 'open' : ''}`}>
            <button
              type="button"
              className="account-ctx-head"
              aria-expanded={ctxOpen}
              onClick={() => setCtxOpen((o) => !o)}
            >
              <Icon name="lock" size={18} className="account-ctx-lock" />
              <span className="account-ctx-title">Captured automatically</span>
              <Icon name={ctxOpen ? 'expand_less' : 'expand_more'} className="account-ctx-chev" />
            </button>
            {ctxOpen && (
              <div className="account-ctx-body">
                <p className="account-ctx-note">
                  We attach this so you do not have to. Dataset contents are never included.
                </p>
                <div className="account-ctx-row">
                  <span className="account-ctx-k">Organisation</span>
                  <span className="account-ctx-v">{orgName}</span>
                </div>
                <div className="account-ctx-row">
                  <span className="account-ctx-k">User</span>
                  <span className="account-ctx-v">{userEmail}</span>
                </div>
                <div className="account-ctx-row">
                  <span className="account-ctx-k">Current page</span>
                  <span className="account-ctx-v">{currentPage}</span>
                </div>
                <div className="account-ctx-row">
                  <span className="account-ctx-k">App version</span>
                  <span className="account-ctx-v">{APP_VERSION}</span>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="account-modal-footer">
          <button type="button" className="account-btn account-btn-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="button" className="account-btn account-btn-primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Sending…' : 'Send request'}
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

export default GetHelpModal;
